<template>
  <v-dialog
    v-model="siteAddressDialog"
    :max-width="modalLayout.MD"
    :data-testid="getTestId('address-dialog')"
    v-bind="modalNoClickAnimViewport"
    @keydown.esc="handleClose"
    @close="handleClose"
    @click:outside="handleClose"
  >
    <v-card class="address-search-dialog-card">
      <CrudFormDialogHeader
        :kicker="$t('site.infoTitle')"
        :title="$t('address.search')"
        show-close
        @close="handleClose"
      />
      <v-card-text class="address-search-dialog-body pt-4 pb-3 px-4">
        <div class="d-flex align-center address-search-bar mb-3">
          <v-text-field
            v-model="keyword"
            class="address-search-input flex-grow-1 mr-2 mb-0"
            hide-details="auto"
            dense
            outlined
            clearable
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('address.searchPlaceholder')"
            :data-testid="getTestId('address-search-input')"
            @keyup.enter="handleSearch"
          />
          <v-btn
            type="button"
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--primary flex-shrink-0"
            :data-testid="getTestId('address-search-button')"
            @click="handleSearch"
          >
            {{ $t('common.search') }}
          </v-btn>
        </div>

        <div class="address-search-results">
          <v-data-table
            :page.sync="page"
            :no-data-text="$t('address.noResults')"
            class="address-search-table elevation-0"
            :items="addressData"
            :headers="[]"
            :items-per-page="itemsPerPage"
            hide-default-footer
            hide-default-header
            @page-count="pageCount = $event"
          >
            <template slot="item" slot-scope="{ item }">
              <div
                class="address-item px-3 py-2"
                :data-testid="getTestId('address-search-result')"
                @click="selectAddress(item)"
              >
                <div class="d-flex align-start address-item__row">
                  <span class="address-item__label flex-shrink-0">{{ $t('address.road') }}</span>
                  <span class="address-item__value text--primary">{{ item.road }}</span>
                </div>
                <div class="d-flex align-start address-item__row mt-1">
                  <span class="address-item__label flex-shrink-0">{{ $t('address.jibun') }}</span>
                  <span class="address-item__value text--primary">{{ item.jibun }}</span>
                </div>
              </div>
            </template>
            <template slot="no-data">
              <div class="address-search-empty text-center py-8 px-4">
                <v-icon color="secondary" class="mb-2">mdi-map-search-outline</v-icon>
                <div class="text-body-2 text--secondary address-search-empty__text">
                  {{ $t('address.noResults') }}
                </div>
              </div>
            </template>
          </v-data-table>
        </div>

        <div
          v-if="pageCount > 1"
          class="d-flex justify-center pt-2"
        >
          <v-pagination
            v-model="page"
            class="address-search-pagination"
            :length="pageCount"
            :total-visible="7"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            @input="handlePageChange"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import axios from 'axios';
import Api from '@/apis/Api';
import { getTestId } from '@/utils/testUtils';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_NO_CLICK_ANIM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'SitesAddressDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    callbackFun: {
      type: Function,
      required: true
    },
    siteAddressDialog: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalNoClickAnimViewport: MODAL_NO_CLICK_ANIM_VIEWPORT,
    addressData: [],
    page: 1,
    totalItems: 0,
    keyword: '',
    itemsPerPage: 5,
    pageCount: 0,
    paging: true,
    jibunJuso: ''
  }),
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },
    handleClose() {
      console.log('주소 검색 다이얼로그 닫기 시도');
      //모달 초기화
      this.resetSiteAddressDialog();
      // Vue 2에서 v-model 업데이트
      this.$emit('input', false);
      this.$emit('update:siteAddressDialog', false);
    },
    resetSiteAddressDialog() {
      this.addressData = [];
      this.page = 1;
      this.totalItems = 0;
      this.keyword = '';
      this.itemsPerPage = 5;
      this.pageCount = 0;
      this.paging = true;
      this.jibunJuso = '';
    },
    handleSearch() {
      this.page = 1; // 검색 시 첫 페이지로 이동
      this.searchAddress(this.page);
    },
    handlePageChange(newPage) {
      this.searchAddress(newPage - 1);
    },
    /**
     * 주소 검색
     * * API를 제공하는 juso.go.kr의 예제{@link https://business.juso.go.kr/addrlink/openApi/searchApi.do}에 따라
     * 쿼리에 SQL 키워드가 포함되어 있는 경우 빈 겁색 결과를 반환합니다.
     *
     * 쿼리에 특수문자{@link specialCharRegex} 가 있는 경우,
     * 특수무자를 제거하여 검색을 합니다.
     *
     * @param {string} input
     * @param {number} page
     *
     * @returns {Promise<{ data: BaseAddress[], page: number, pageCount: number, totalItems: number }>}
     *
     */
    async searchAddress(page) {
      if (!this.keyword.trim()) {
        alert(this.$t('site.addressLookup.enterKeyword'));
        return;
      }

      const countPerPage = 5;
      const getBaseAddress = (juso) => ({
        jibun: juso.jibunAddr,
        road: juso.roadAddr
      });
      const url = `https://business.juso.go.kr/addrlink/addrLinkApi.do?keyword=${this.keyword}&confmKey=${process.env.VUE_APP_JUSO_SEARCH_KEY}&currentPage=${this.page}&countPerPage=${countPerPage}&resultType=json`;
      try {
        const response = await axios.get(url);
        if (response.data.results.common.errorCode !== '0') {
          alert(
            response.data.results.common.errorMessage ??
              this.$t('site.addressLookup.searchApiError')
          );
          return;
        }
        if (response.data.results.common.totalCount > 9000) {
          alert(this.$t('site.addressLookup.enterDetailAddress'));
          return;
        }
        const totalItems = +response.data.results.common?.totalCount ?? 0;
        this.addressData =
          response.data.results.juso?.map(getBaseAddress) ?? [];
        this.totalItems = totalItems;
        this.pageCount = this.getPageCount(totalItems, countPerPage);
      } catch (e) {
        console.error(e);
      }
    },
    getPageCount(totalCount, countPerPage) {
      if (totalCount === 0 || countPerPage == 0) return 1;
      const hasRest = totalCount % countPerPage != 0;
      return Math.floor(totalCount / countPerPage + (hasRest ? 1 : 0));
    },
    /**
     * 지번주소를 부모 dialog로 전송
     * @return {altitude, latitude, addressName}
     */
    async selectAddress(item) {
      console.log('주소 선택 시작:', item);
      //서버로 부터  vworld /api/geocoder/getcoordbyaddr?address=item.jibun 을 통해 좌표를 받아서 부모 dialog로 전송
      //java 서버로 vworld 좌표 정보 get
      if (!item.jibun) alert(this.$t('site.addressLookup.invalidJibun'));

      let positionResponse = await Api.read(
        `/geocoder/getcoord?address=${item.jibun}&&addrType=PARCEL`
      );

      console.log('positionResponse', positionResponse);
      //지번주소로 좌표를 받아오지 못한 경우 도로명 주소로 좌표를 받아옴
      if (positionResponse.data.content == null) {
        positionResponse = await Api.read(
          `/geocoder/getcoord?address=${item.road}&&addrType=ROAD`
        );
      }

      // 좌표를 받아오지 못한 경우
      if (positionResponse.data.content == null) {
        alert(this.$t('site.addressLookup.noCoordinates'));
        return;
      }

      const longitude = positionResponse.data?.content?.x ?? 0; //경도 127
      const latitude = positionResponse.data?.content?.y ?? 0; //위도 34

      console.log('좌표 정보 받음:', { longitude, latitude, addressName: item?.jibun });

      this.$props.callbackFun({
        latitude: latitude,
        longitude: longitude,
        addressName: item?.jibun
      });

      console.log('콜백 함수 호출 완료, 다이얼로그 닫기 시도');

      // Vue 2 방식으로 다이얼로그 닫기
      this.$emit('input', false);
      this.$emit('update:siteAddressDialog', false);
      
      // 모달 초기화
      this.resetSiteAddressDialog();
      
      // 강제로 DOM 업데이트
      this.$forceUpdate();
    }
  }
};
</script>
<style scoped>
.address-search-dialog-card {
  background-color: var(--v-surface-base) !important;
  border-color: var(--v-borderSubtle-base) !important;
}

.address-search-dialog-body {
  min-height: 0;
}

.address-search-bar {
  gap: 0;
}

.address-search-results {
  min-height: 220px;
  border: 1px solid var(--v-borderSubtle-base);
  border-radius: 6px;
  background-color: color-mix(in srgb, var(--v-surface-base) 94%, var(--v-text-base));
  overflow: hidden;
}

.address-search-table {
  background-color: transparent !important;
}

.address-search-table ::v-deep .v-data-table__wrapper {
  overflow-x: hidden;
}

.address-search-table ::v-deep table {
  table-layout: fixed;
  width: 100%;
}

.address-search-table ::v-deep tbody tr {
  background: transparent !important;
}

.address-search-table ::v-deep .v-data-table__empty-wrapper {
  padding: 0 !important;
}

.address-search-empty__text {
  white-space: pre-line;
  line-height: 1.45;
  max-width: 20rem;
  margin-left: auto;
  margin-right: auto;
}

.address-item {
  cursor: pointer;
  border-bottom: 1px solid var(--v-borderSubtle-base);
  transition: background-color 0.12s ease;
}

.address-item:last-child {
  border-bottom: none;
}

.address-item:hover {
  background-color: color-mix(in srgb, var(--v-primary-base) 10%, var(--v-surface-base));
}

.address-item__label {
  width: 5.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--v-secondary-base);
  line-height: 1.4;
  padding-right: 8px;
}

.address-item__value {
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.address-search-pagination ::v-deep .v-pagination__item,
.address-search-pagination ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid var(--v-borderSubtle-base) !important;
  background-color: var(--v-surface-base) !important;
  color: var(--v-text-base) !important;
}

.address-search-pagination ::v-deep .v-pagination__item--active {
  border-color: var(--v-primary-base) !important;
  color: var(--v-primary-base) !important;
  background-color: color-mix(in srgb, var(--v-primary-base) 14%, var(--v-surface-base)) !important;
}
</style>
